import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { getDefaultFlowMeta } from '../../api/agentFlowSchema';
import { TRIGGER_KIND_OPTIONS, TRIGGER_REF_PLACEHOLDER } from './agentFlowCatalog';

const K = { navy: '#121826', blue: '#4185F1', border: 'rgba(18, 24, 38, 0.12)' };

export function AgentFlowTriggerContextPanel({ flowMeta, setFlowMeta, kobraFieldSx }) {
  const meta = flowMeta || getDefaultFlowMeta();

  const patchTrigger = (patch) => {
    setFlowMeta((m) => {
      const base = m || getDefaultFlowMeta();
      return {
        ...base,
        trigger: { ...base.trigger, ...patch },
      };
    });
  };

  const bindings = meta.context?.bindings || [];

  const setBindings = (next) => {
    setFlowMeta((m) => {
      const base = m || getDefaultFlowMeta();
      return {
        ...base,
        context: { ...base.context, bindings: next },
      };
    });
  };

  const addBinding = () => {
    setBindings([...bindings, { key: '', source: '' }]);
  };

  const updateBinding = (index, field, value) => {
    const next = bindings.map((b, i) => (i === index ? { ...b, [field]: value } : b));
    setBindings(next);
  };

  const removeBinding = (index) => {
    setBindings(bindings.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={1}>
      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{ border: `1px solid ${K.border}`, borderRadius: 1.5, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: K.navy }}>
            1 · Trigger
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Qué activa al agente (evento, regla o programación).
          </Typography>
          <FormControl fullWidth size="small" sx={{ ...kobraFieldSx, mb: 1.25 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              label="Tipo"
              value={meta.trigger?.kind || 'manual'}
              onChange={(e) => patchTrigger({ kind: e.target.value })}
            >
              {TRIGGER_KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Ref. catálogo (opcional)"
            fullWidth
            size="small"
            placeholder={TRIGGER_REF_PLACEHOLDER}
            value={meta.trigger?.ref ?? ''}
            onChange={(e) => patchTrigger({ ref: e.target.value.trim() || null })}
            sx={{ ...kobraFieldSx, mb: 1.25 }}
            helperText="ID estable cuando exista catálogo en backend"
          />
          <TextField
            label="Descripción / resumen"
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Ej. Disparar cuando la cuota vence en 24h y hay saldo pendiente"
            value={meta.trigger?.summary ?? ''}
            onChange={(e) => patchTrigger({ summary: e.target.value })}
            sx={kobraFieldSx}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion
        defaultExpanded
        disableGutters
        elevation={0}
        sx={{ border: `1px solid ${K.border}`, borderRadius: 1.5, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: K.navy }}>
            2 · Contexto
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Qué información ve el agente antes de decidir (referencias; el motor resuelve valores).
          </Typography>
          {bindings.length === 0 && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: alpha(K.navy, 0.45) }}>
              Sin bindings. Añade clave y origen declarado.
            </Typography>
          )}
          {bindings.map((b, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 0.75,
                alignItems: 'flex-start',
                mb: 1,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                label="Clave"
                size="small"
                placeholder="saldo_actual"
                value={b.key}
                onChange={(e) => updateBinding(index, 'key', e.target.value)}
                sx={{ ...kobraFieldSx, flex: 1, minWidth: 100 }}
              />
              <TextField
                label="Origen"
                size="small"
                placeholder="ledger.balance"
                value={b.source}
                onChange={(e) => updateBinding(index, 'source', e.target.value)}
                sx={{ ...kobraFieldSx, flex: 1, minWidth: 120 }}
              />
              <IconButton size="small" aria-label="Quitar" onClick={() => removeBinding(index)} sx={{ mt: 0.5 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addBinding}
            sx={{ textTransform: 'none', fontWeight: 600, color: K.blue }}
          >
            Añadir binding
          </Button>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
